<script>
  import { flip } from 'svelte/animate'
  import { fade } from 'svelte/transition'

  import { updateUrlQueryParams, getQueryParams, CONSTS, iterateWithIndex } from './utils'
  import { getCurrentFilterObject, initializeFilter } from './filter'

  import { paginate } from './paginate'
  import GalleryCarousel from './GalleryCarousel.svelte'
  import PaginateNav from './paginate/PaginateNav.svelte'
  import './styles.scss'
  import { getSizedImageUrl } from '@shopify/theme-images'

  export let albumId
  export let config
  export let albums // album items
  export let albumRoot
  export let filter_tree
  export let filter_tree_by_keys
  export let widget_type

  let onEvent = config.onEvent || (() => {})

  if (!config) throw new Error('Please provide the config')

  let resetScrollOnGallery = config.resetScrollOnGallerySelect
  let resetScrollOnPaginate = config.resetScrollOnPaginate
  let albumClass = config.album?.cssClass || ''
  let filterSectionHeading = config.album?.filterSectionHeading || 'Filter'
  let goBackClass = config.gallery?.goBackCssClass || ''
  let viewProductsClass = config.gallery?.viewProductsClass || ''
  let galleryId = config.updateUrl ? getQueryParams(CONSTS.GLRY_QP) : null
  let currentPage = 1
  let pageSize = config.perPage
  let navigated = false

  // [ [label, [option, option]] , ... ]
  let FILTER_LABELS_LIST = config.album.filterKeys || []
  $: paginatedAlbums = paginate({ items: filteredAlbums, pageSize, currentPage })

  function getGalleryProductsHref(selectedGallery, galleryId) {
    try {
      if (!selectedGallery) return null
      const hrefGetter = window._xoGalleryProductHref
      if (typeof hrefGetter !== 'function') return null
      let href = hrefGetter(selectedGallery, galleryId)
      return typeof href === 'string' ? href : null
    } catch (e) {
      return null
    }
  }

  $: selectedGallery =
    Array.isArray(albums) && galleryId != null
      ? albums.find(al => al.id == galleryId || al.gId == galleryId)
      : null
  $: viewGalleryProductsHref =
    selectedGallery && galleryId ? getGalleryProductsHref(selectedGallery, galleryId) : null
  $: {
    if (selectedGallery) {
      selectedGallery._galleryTitle = selectedGallery.title?.en || selectedGallery.title
    }
  }

  const sp = new URLSearchParams(window.location.search)
  const initialActiveFilters = FILTER_LABELS_LIST.reduce((acc, k, i) => {
    try {
      acc[k] = sp.get(k) || sp.get(k.toLowerCase()) || ''
    } catch (e) {
      acc[k] = ''
    }
    return acc
  }, {})

  let activeFilters = initializeFilter(
    FILTER_LABELS_LIST,
    filter_tree,
    config.album?.initialFilters || initialActiveFilters,
  )

  $: activeFiltersList = [...activeFilters]

  $: filteredAlbums =
    activeFilters.size > 0 && albums
      ? albums.filter(al =>
          activeFiltersList.every(
            ([key, value]) => value === '' || al[CONSTS.FILTER_KEY]?.[key] === value,
          ),
        )
      : albums

  // $: window.albums = albums
  // $: window.filteredAlbums = filteredAlbums
  // $: window.activeFilters = activeFilters
  // $: window.FILTER_TREE = FILTER_TREE

  $: {
    // Update query params whenver the gallery changes
    if (config.updateUrl) {
      updateUrlQueryParams(
        {
          [CONSTS.GLRY_QP]: galleryId,
        },
        false,
      )
    }
  }
  $: {
    // Update query params whenver the gallery changes
    if (config.updateUrl) {
      updateUrlQueryParams(
        FILTER_LABELS_LIST.reduce((acc, k, i) => {
          acc[k.toLowerCase()] = activeFilters.get(k)
          return acc
        }, {}),
        true,
      )
    }
  }

  // Listen on 'back'
  if (config.updateUrl) {
    window.addEventListener(
      'popstate',
      () => {
        galleryId = getQueryParams(CONSTS.GLRY_QP)
        if (!galleryId) {
          onEvent(CONSTS.EVENT.BACK_TO_ALBUM, { albumId })
        }
      },
      false,
    )
  }

  function onGalleryClose() {
    onEvent(CONSTS.EVENT.BACK_TO_ALBUM, { albumId })

    if (config.updateUrl && navigated) {
      // Triggers popstate
      history.back()
    } else {
      galleryId = null
    }
  }

  /**@type HTMLElement */
  function onPaginate(nextPageNumber) {
    onEvent(CONSTS.EVENT.ALBUM_PAGINATED, { from: currentPage, to: nextPageNumber })

    currentPage = nextPageNumber

    // let firstAlbum = albumRoot.querySelector('.XOC_Album--items .XOC_Album--item')
    // if (firstAlbum) {
    //   firstAlbum.scrollIntoView({ behavior: 'smooth' })
    // }
    if (resetScrollOnPaginate) {
      albumRoot.scrollIntoView({ behavior: 'smooth' })
    }
  }

  function collectKeysAtLevel(tree, keys, selectedFilters, targetLevel, currentLevel = 0) {
    if (!tree || typeof tree !== 'object') return []

    // Reached the level we care about
    if (currentLevel === targetLevel) {
      return tree._keys_ || []
    }

    const key = keys[currentLevel]
    const selectedValue = selectedFilters.get(key)
    const nextTrees = []

    if (selectedValue) {
      if (tree[selectedValue]) {
        nextTrees.push(tree[selectedValue])
      }
    } else {
      if (tree?._keys_) {
        for (const k of tree._keys_) {
          if (tree[k]) {
            nextTrees.push(tree[k])
          }
        }
      }
    }

    const collected = new Set()

    for (const subtree of nextTrees) {
      const subKeys = collectKeysAtLevel(
        subtree,
        keys,
        selectedFilters,
        targetLevel,
        currentLevel + 1,
      )
      for (let i = 0, l = subKeys.length; i < l; i++) {
        const k = subKeys[i]
        collected.add(k)
      }
    }

    return Array.from(collected).sort((a, b) => a.localeCompare(b))
  }

  const cache = new Map()
  const MAX_CACHE = 50

  function memoizedCollectKeysAtLevel(tree, keys, selectedFilters, targetLevel) {
    const cacheKey = JSON.stringify({
      filters: [...selectedFilters.entries()],
      level: targetLevel,
    })

    if (cache.has(cacheKey)) {
      return cache.get(cacheKey)
    }

    const result = collectKeysAtLevel(tree, keys, selectedFilters, targetLevel)

    if (cache.size >= MAX_CACHE) {
      // Simple LRU eviction
      const oldestKey = cache.keys().next().value
      cache.delete(oldestKey)
    }

    cache.set(cacheKey, result)
    return result
  }

  function getFacetDataV2({ label, idx, activeFilters, activeFiltersList, filter_tree }) {
    const tree = filter_tree_by_keys[label]

    // Start from root tree (e.g. tree for 'Year')
    const filterOptions = memoizedCollectKeysAtLevel(
      filter_tree_by_keys[FILTER_LABELS_LIST[0]], // root tree
      FILTER_LABELS_LIST, // keys list
      activeFilters, // Map of selected filters
      idx, // current filter's depth
    )

    const hasFilterOptions = filterOptions.length > 0

    return { filterOptions, hasFilterOptions, tree }
  }

  function getFacetData({ label, idx, activeFilters, activeFiltersList, filter_tree }) {
    const filterObject = getCurrentFilterObject({
      selectedValues: activeFiltersList,
      index: idx,
      transformedFiltersObject: filter_tree,
    })
    const filterOptions = filterObject?._keys_
    const hasFilterOptions = Array.isArray(filterOptions) && filterOptions.length > 0
    return { filterOptions, hasFilterOptions, tree: null }
  }
</script>

<div class="XOC_Album_Root" bind:this={albumRoot}>
  <div class="XOC_Album" style={galleryId ? `display: none;` : ''}>
    {#if filter_tree && Array.isArray(filter_tree._keys_) && filter_tree._keys_.length > 0}
      <div class="XOC_Album--filters-section">
        <div class="XOC_Album--filter-heading">{filterSectionHeading}</div>

        <div class="XOC_Album--filters">
          {#each FILTER_LABELS_LIST as label, idx}
            {@const { filterOptions, hasFilterOptions, tree } =
              widget_type == 'v2'
                ? getFacetDataV2({
                    label,
                    idx,
                    activeFiltersList,
                    activeFilters,
                  })
                : getFacetData({ label, idx, activeFilters, activeFiltersList, filter_tree })}

            <div class="XOC_Album--filter" data-filter-selectable={hasFilterOptions}>
              <label for={`XOC_Filter_${albumId}_${idx}`}>{label}</label>

              <select
                id={`XOC_Filter_${albumId}_${idx}`}
                on:change={e => {
                  let v = e.target.value
                  v ? activeFilters.set(label, v) : activeFilters.set(label, '')

                  iterateWithIndex(([key, value], i) => {
                    if (i > idx) {
                      // ensure we only reset next selected filter if the combination is invalid
                      if (!tree || !tree[v]?.[value]) {
                        activeFilters.set(key, '')
                      }
                    }
                  }, activeFilters)

                  activeFilters = activeFilters
                  currentPage = 1
                }}
                value={activeFilters.get(label) || ''}
                disabled={!hasFilterOptions}
              >
                <option value="">{'All'}</option>

                {#if hasFilterOptions}
                  {#each filterOptions as option (option)}
                    <option value={option}>{option}</option>
                  {/each}
                {/if}
              </select>
            </div>
          {/each}
        </div>
      </div>
    {/if}

    <div class="XOC_Album--items">
      {#each paginatedAlbums as album (album._id)}
        <div
          class={`XOC_Album--item ${albumClass}`}
          animate:flip={{
            duration: 400,
          }}
          transition:fade
        >
          <div class="XOC_Album--image-wrap">
            <img
              class="lazyloaded"
              src={getSizedImageUrl(album.images?.[0]?.url || album.url, 'x300')}
              alt=""
            />
          </div>

          <div class="XOC_Album--item-content">
            <h3 class="h3 XOC_Album--item-title">{album.title}</h3>
          </div>

          <span class="XOC_Album--icon" />

          <button
            class="XOC_OverlayBtn"
            on:click={() => {
              galleryId = album.id
              if (!navigated) navigated = true
              if (resetScrollOnGallery) {
                document.body.scrollIntoView({ behavior: 'smooth' })
              }

              const selected = albums.find(al => al.gId == galleryId)
              onEvent(CONSTS.EVENT.GALLERY_SELECTED, { galleryId: galleryId, gallery: selected })
            }}
          />
        </div>
      {/each}
    </div>

    {#if filteredAlbums.length > pageSize}
      <PaginateNav
        totalItems={filteredAlbums.length}
        {pageSize}
        limit={2}
        showStepOptions={true}
        on:setPage={e => {
          onPaginate(e.detail.page)
        }}
        {currentPage}
      />
    {/if}
  </div>

  {#if galleryId}
    <GalleryCarousel
      {galleryId}
      {config}
      {onGalleryClose}
      galleryInfo={selectedGallery}
      let:galleryTitle
      let:galleryDescription
    >
      <slot slot="header">
        <div class="XOC_Gal--header">
          {#if galleryTitle}
            <h2 class="h2 XOC_Gal--title">
              {galleryTitle}
            </h2>
          {/if}

          {#if galleryDescription}
            <div class="XOC_Gal--desc">{@html galleryDescription}</div>
          {/if}

          <div class="XOC_GoBack--wrapper">
            <button class={`XOC_GoBack ${goBackClass}`} on:click={onGalleryClose}>
              <span class="btn-text">Go back</span>
            </button>

            {#if viewGalleryProductsHref}
              <a
                class={`XOC_GoBack XOC_GotoProductsListing ${viewProductsClass}`}
                href={viewGalleryProductsHref}>{config.viewProductsText || 'View Products'}</a
              >
            {/if}
          </div>
        </div>
      </slot>
    </GalleryCarousel>
  {/if}
</div>
